import { useState, useEffect, useRef } from 'react';
import { Bell, Check, CheckCheck, Calendar, MessageCircle, Star, UserCheck, X, BellRing, Settings, Wifi, WifiOff } from 'lucide-react';
import { notificationsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import pushService from '../lib/pushNotifications';
import wsNotificationsService from '../services/wsNotifications';

const NOTIFICATION_ICONS = {
    booking_confirmed: Calendar,
    booking_cancelled: X,
    booking_completed: Star,
    provider_approved: UserCheck,
    new_message: MessageCircle,
    new_review: Star,
};

const NOTIFICATION_COLORS = {
    booking_confirmed: 'text-green-500 bg-green-50',
    booking_cancelled: 'text-red-500 bg-red-50',
    booking_completed: 'text-yellow-500 bg-yellow-50',
    provider_approved: 'text-blue-500 bg-blue-50',
    new_message: 'text-[#4A90B2] bg-[#4A90B2]/10',
    new_review: 'text-amber-500 bg-amber-50',
};

export default function NotificationBell() {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [pushEnabled, setPushEnabled] = useState(false);
    const [pushSupported, setPushSupported] = useState(false);
    const [permissionStatus, setPermissionStatus] = useState('default');
    const [wsConnected, setWsConnected] = useState(false);
    const dropdownRef = useRef(null);
    const previousUnreadCount = useRef(0);

    // Initialize push notifications
    useEffect(() => {
        const initPush = async () => {
            const supported = pushService.isSupported();
            setPushSupported(supported);
            
            if (supported) {
                await pushService.init();
                setPushEnabled(pushService.isPushEnabled());
                setPermissionStatus(pushService.getPermissionStatus());
            }
        };
        initPush();
    }, []);

    useEffect(() => {
        if (user) {
            loadNotifications();
            
            // Conecta WebSocket para notificações em tempo real
            wsNotificationsService.onConnectionChange((connected) => {
                setWsConnected(connected);
            });

            wsNotificationsService.onNotification((notification) => {
                // Adiciona nova notificação no topo da lista
                setNotifications(prev => {
                    const exists = prev.some(n => n.id === notification.id);
                    if (exists) return prev;
                    return [{
                        id: notification.id,
                        type: notification.event_type || notification.type,
                        title: notification.title,
                        message: notification.message,
                        data: notification.data,
                        read: false,
                        created_at: notification.timestamp
                    }, ...prev].slice(0, 50);
                });
                setUnreadCount(prev => prev + 1);
            });

            wsNotificationsService.onInitialNotifications((data) => {
                if (data.notifications) {
                    setNotifications(data.notifications);
                    setUnreadCount(data.unread_count || 0);
                }
            });

            // Conecta ao WebSocket
            wsNotificationsService.connect(user.id);
            
            // Poll para sincronização periódica (fallback)
            const interval = setInterval(loadNotifications, 60000);
            
            return () => {
                clearInterval(interval);
                wsNotificationsService.disconnect();
            };
        }
    }, [user]);

    useEffect(() => {
        // Close dropdown when clicking outside
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setShowSettings(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Show push notification when new notifications arrive
    useEffect(() => {
        if (pushEnabled && unreadCount > previousUnreadCount.current && notifications.length > 0) {
            const latestNotification = notifications.find(n => !n.read);
            if (latestNotification && latestNotification.message) {
                pushService.showLocalNotification(latestNotification.title || 'Notificação', {
                    body: (latestNotification.message || '').split('\n')[0],
                    tag: latestNotification.id,
                    data: { url: '/', notificationId: latestNotification.id }
                });
            }
        }
        previousUnreadCount.current = unreadCount;
    }, [unreadCount, notifications, pushEnabled]);

    const loadNotifications = async () => {
        try {
            const response = await notificationsAPI.list(false, 20);
            setNotifications(response.data.notifications || []);
            setUnreadCount(response.data.unread_count || 0);
        } catch (error) {
            console.error('Error loading notifications:', error);
        }
    };

    const handleMarkRead = async (notificationId) => {
        try {
            await notificationsAPI.markRead(notificationId);
            setNotifications(notifications.map(n => 
                n.id === notificationId ? { ...n, read: true } : n
            ));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const handleMarkAllRead = async () => {
        setLoading(true);
        try {
            await notificationsAPI.markAllRead();
            setNotifications(notifications.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all as read:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleTogglePush = async () => {
        if (pushEnabled) {
            await pushService.unsubscribe();
            setPushEnabled(false);
        } else {
            const permission = await pushService.requestPermission();
            setPermissionStatus(permission);
            
            if (permission === 'granted') {
                const subscription = await pushService.subscribe();
                if (subscription) {
                    setPushEnabled(true);
                    // Show a test notification
                    pushService.showLocalNotification('Notificações Ativadas! 🔔', {
                        body: 'Você receberá alertas mesmo com o navegador minimizado.',
                        tag: 'push-enabled'
                    });
                }
            }
        }
    };

    const formatTime = (dateStr) => {
        try {
            return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: ptBR });
        } catch {
            return '';
        }
    };

    if (!user) return null;

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => { setIsOpen(!isOpen); setShowSettings(false); }}
                className="relative p-2 text-slate-600 hover:text-[#4A90B2] hover:bg-slate-100 rounded-xl transition-colors"
                data-testid="notification-bell"
            >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
                {/* Indicador de conexão WebSocket */}
                <span 
                    className={`absolute bottom-0 right-0 w-2 h-2 rounded-full border border-white ${
                        wsConnected ? 'bg-green-500' : 'bg-gray-400'
                    }`}
                    title={wsConnected ? 'Tempo real ativo' : 'Reconectando...'}
                />
            </button>

            {isOpen && (
                <div className="fixed sm:absolute inset-x-2 sm:inset-x-auto sm:right-0 top-auto mt-2 sm:w-96 bg-white rounded-2xl shadow-xl border border-slate-100 z-50 overflow-hidden max-h-[80vh] sm:max-h-none">
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-slate-100">
                        <h3 className="font-bold text-slate-800">Notificações</h3>
                        <div className="flex items-center gap-2">
                            {unreadCount > 0 && (
                                <button
                                    onClick={handleMarkAllRead}
                                    disabled={loading}
                                    className="text-sm text-[#4A90B2] hover:underline flex items-center gap-1"
                                    title="Marcar todas como lidas"
                                >
                                    <CheckCheck className="w-4 h-4" />
                                </button>
                            )}
                            <button
                                onClick={() => setShowSettings(!showSettings)}
                                className={`p-1.5 rounded-lg transition-colors ${showSettings ? 'bg-[#4A90B2] text-white' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
                                title="Configurações"
                                data-testid="notification-settings-btn"
                            >
                                <Settings className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Settings Panel */}
                    {showSettings && (
                        <div className="p-4 bg-slate-50 border-b border-slate-100">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${pushEnabled ? 'bg-green-100 text-green-600' : 'bg-slate-200 text-slate-500'}`}>
                                        <BellRing className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-800 text-sm">Notificações Push</p>
                                        <p className="text-xs text-slate-500">
                                            {!pushSupported 
                                                ? 'Use o Chrome ou instale o app para receber notificações'
                                                : permissionStatus === 'denied'
                                                ? 'Bloqueado - vá em Configurações do navegador para permitir'
                                                : pushEnabled 
                                                ? 'Alertas mesmo com o app fechado' 
                                                : 'Receba alertas em tempo real'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={handleTogglePush}
                                    disabled={!pushSupported || permissionStatus === 'denied'}
                                    className={`relative w-12 h-6 rounded-full transition-colors ${
                                        !pushSupported || permissionStatus === 'denied'
                                            ? 'bg-slate-200 cursor-not-allowed'
                                            : pushEnabled 
                                            ? 'bg-green-500' 
                                            : 'bg-slate-300'
                                    }`}
                                    data-testid="push-toggle"
                                >
                                    <span 
                                        className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${
                                            pushEnabled ? 'translate-x-7' : 'translate-x-1'
                                        }`}
                                    />
                                </button>
                            </div>
                            {permissionStatus === 'denied' && (
                                <p className="mt-2 text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                                    Para ativar, clique no cadeado na barra de endereço e permita notificações.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Notifications list */}
                    <div className="max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="p-8 text-center">
                                <Bell className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                                <p className="text-slate-500">Nenhuma notificação</p>
                                {pushSupported && !pushEnabled && (
                                    <button
                                        onClick={() => setShowSettings(true)}
                                        className="mt-3 text-sm text-[#4A90B2] hover:underline"
                                    >
                                        Ativar notificações push
                                    </button>
                                )}
                            </div>
                        ) : (
                            notifications.map((notification) => {
                                const Icon = NOTIFICATION_ICONS[notification.type] || Bell;
                                const colorClass = NOTIFICATION_COLORS[notification.type] || 'text-slate-500 bg-slate-50';
                                
                                return (
                                    <div
                                        key={notification.id}
                                        className={`p-4 border-b border-slate-50 hover:bg-slate-50 transition-colors ${
                                            !notification.read ? 'bg-[#F0F9FF]' : ''
                                        }`}
                                        data-testid={`notification-${notification.id}`}
                                    >
                                        <div className="flex gap-3">
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                                                <Icon className="w-5 h-5" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                    <p className="font-semibold text-sm text-slate-800 line-clamp-1">
                                                        {notification.title}
                                                    </p>
                                                    {!notification.read && (
                                                        <button
                                                            onClick={() => handleMarkRead(notification.id)}
                                                            className="text-[#4A90B2] hover:text-[#3A7A9F] flex-shrink-0"
                                                            title="Marcar como lida"
                                                        >
                                                            <Check className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                                <p className="text-sm text-slate-600 line-clamp-2 mt-1">
                                                    {(notification.message || '').split('\n')[0] || 'Sem mensagem'}
                                                </p>
                                                <p className="text-xs text-slate-400 mt-1">
                                                    {formatTime(notification.created_at)}
                                                    {notification.email_sent && (
                                                        <span className="ml-2 text-green-500">• Email enviado</span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Footer with push and websocket status */}
                    <div className="px-4 py-2 bg-slate-50 border-t border-slate-100">
                        <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
                            <div className="flex items-center gap-1.5">
                                {wsConnected ? (
                                    <Wifi className="w-3 h-3 text-green-500" />
                                ) : (
                                    <WifiOff className="w-3 h-3 text-gray-400" />
                                )}
                                <span>{wsConnected ? 'Tempo real' : 'Reconectando'}</span>
                            </div>
                            {pushSupported && (
                                <div className="flex items-center gap-1.5">
                                    <span className={`w-2 h-2 rounded-full ${pushEnabled ? 'bg-green-500' : 'bg-slate-300'}`} />
                                    <span>{pushEnabled ? 'Push ativo' : 'Push off'}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
